// FlowsApp.jsx — Top-level orchestrator for Bay Flows (home-base lens, Phase 1 T8)
// Manages state, data loading, the §1 first-run state machine, persistence,
// Dijkstra (with route tracking), event matching, and layout.
//
// GPS fires ONLY on tap of FIND MY STATION (gesture gate) — never on mount.
// Coords are used transiently to find the nearest station, then discarded;
// only the station id persists (lib/home-base.js owns the storage contract).

const { useState, useEffect, useCallback, useRef, useMemo } = React;

// Lens palette — spec §0.5, defined once per file, never re-derived.
// T8 surfaces only need the surface tokens + reach1 (prompt/chip accents).
const LENS = {
  dark: {
    page: '#0C0F0F', panel: '#1A1C1C', ink: '#E9E7E2', muted: '#94897B',
    hairline: 'rgba(255,255,255,0.08)', reach1: '#FF6E32',
  },
  light: {
    page: '#F5F2EC', panel: '#FBF9F4', ink: '#191512', muted: '#6B5B4F',
    hairline: 'rgba(26,23,20,0.12)', reach1: '#BF3B00',
  },
};

// Copy — spec §7, verbatim. Single place so Rhea-review diffs one block.
const COPY = {
  P1: 'SET YOUR HOME BASE',
  P2: 'Your nearest rail stop becomes the lens — every show tonight gets a time from home. Your location never leaves this phone; we only keep the station.',
  // P3 — the storage line under the home-base popover. CONDITIONAL, and the
  // condition is the whole point. "Stored on this device only." is TRUE for an
  // unattached browser and FALSE the moment a setup is claimed, because
  // commitHome's write-through pushes homeStation to the server
  // (lib/account-sync.js PROFILE_FIELDS). Shipped unconditionally it put a
  // device-only denial SEVENTEEN LINES from AFY_SCOPE's "Clears it here and
  // from your setup." — inside the same role="dialog". One of the two had to
  // become conditional, and it is not the one describing the real storage.
  //
  // The split mirrors the claimed-vs-not shape /privacy already uses (R4/R5),
  // and the attached variant reuses AFY_SCOPE's exact two locations ("here" /
  // "your setup") in the same order, so the two lines in that dialog now
  // describe ONE storage model instead of contradicting each other.
  //
  // ⚠ SENTENCE TWO IS UNCONDITIONAL AND STAYS BYTE-IDENTICAL. Coordinates are
  // used transiently to find the nearest station and are discarded — only the
  // station id ever persists (see this file's header and commitHome). It is
  // true attached or not, and it is the disclosure that actually earns trust.
  // Do not fold it into the conditional and do not "simplify" it away.
  P3_DEVICE_ONLY: 'Stored on this device only.',
  P3_ATTACHED: 'Stored here and in your setup.',
  P3_COORDS: 'Your exact location never leaves your phone.',
  FIND: 'FIND MY STATION', PICK: 'PICK IT MYSELF', NOT_NOW: 'NOT NOW',
  P5: 'FINDING YOUR STATION…',
  THATS_ME: "THAT'S ME", PICK_ANOTHER: 'PICK ANOTHER',
  P7_H: 'NO GPS? NO STRESS.', P7_SUB: 'Pick your station — the lens works the same.',
  P8_H: "YOU'RE OUTSIDE THE BAY RIGHT NOW",
  P8_SUB: 'Browse the whole map, or set a home base for when you touch down.',
  JUST_BROWSING: 'JUST BROWSING', PICK_A_STATION: 'PICK A STATION',
  H2_GHOST: 'SET A HOME BASE +',
  CHANGE_HOME: 'CHANGE HOME', FORGET: 'FORGET MY HOME',
  M1_HOOK: 'EVERY SHOW YOU CAN REACH TONIGHT',
  M1_SUB: 'Get to the culture. Every show measured from your home station — down to the last train back.',
  M3: 'Bay Area transit map. Everything on it is listed in the feed.',
  // Deep-link (Phase 2 §4) — addendum copy block, verbatim.
  CLOCKING: 'CLOCKING: ',                                                        // N1 prefix (+ TITLE)
  N2: "THAT ONE'S GONE — MIGHT'VE PASSED. HERE'S TONIGHT INSTEAD.",              // notice — not found
  N3: "THAT SPOT'S OFF THE RAIL GRID — NO TRAIN PATH TO CLOCK.",                 // notice — off-grid
  // Live-region strings — L2/L3/L4 verbatim; L1 built inline (title + minutes).
  LIVE_LINK_NOT_FOUND: "That event has passed or can't be found — showing tonight instead",
  LIVE_LINK_OFF_GRID: "No rail path to that venue — showing tonight's reachable events instead",
  DISMISS_NOTICE: 'Dismiss notice',
  // Live region strings — §6.5 verbatim (X2)
  LIVE_FINDING: 'Finding your station…',
  LIVE_SET: 'Home base set: ',
  LIVE_DENIED: 'GPS unavailable — pick your station from the list',
  LIVE_OUTSIDE: 'You look outside the Bay — browse or pick a station',
  LIVE_FORGOT: 'Home base forgotten',
  // Account-lite (Rhea FINAL) — the contextual door and the FORGET scope
  // sub-line. The door is a STANDING door, not a prompt: no delay, no
  // appearance animation, no dismissal state to manage (nothing to decline,
  // so decline-is-terminal is satisfied vacuously). It renders only while
  // unattached; the sub-line only while attached.
  AFY_DOOR: 'CARRY IT ACROSS PHONES',
  AFY_SCOPE: 'Clears it here and from your setup.',
};

// Goal 5 — hero pill text. Home set + live count → "{N} SHOWS REACHABLE TONIGHT"
// (the within-the-hour count the feed already computes — LONG HAUL excluded, so
// the hero can never disagree with the feed). Any other home state, or no count
// yet, → the value-prop hook. Pure + window-attached so tests pin the contract.
function flowsHeroPill(homeState, reachableCount) {
  if (homeState === 'set' && typeof reachableCount === 'number') {
    return `${reachableCount} ${reachableCount === 1 ? 'SHOW' : 'SHOWS'} REACHABLE TONIGHT`;
  }
  return COPY.M1_HOOK;
}

// P3's storage line. Pure + window-attached so the honesty contract is pinnable
// without mounting, exactly like flowsHeroPill above. The attachment flag is an
// ARGUMENT, not a module read: that is what makes the gate in
// tests/device-only-claims.test.js able to prove the device-only clause is
// unreachable while attached, rather than merely absent from one rendered state.
function flowsStorageLine(attached) {
  return (attached ? COPY.P3_ATTACHED : COPY.P3_DEVICE_ONLY) + ' ' + COPY.P3_COORDS;
}

function FlowsApp() {
  // ---------------------------------------------------------------------------
  // Dark mode + radius — mirrors main App pattern from index.html
  // ---------------------------------------------------------------------------
  const TWEAK_DEFAULTS = { darkMode: true, accentColor: '#FF4F00', glassRadius: 32 };

  const [tweaks, setTweaks] = useState(() => {
    try {
      const saved = localStorage.getItem('onsumn-tweaks-v2');
      if (!saved) return TWEAK_DEFAULTS;
      const parsed = JSON.parse(saved);
      const VALID_COLORS = ['#FF4F00', '#D57800', '#FF5717', '#395542', '#7A99AC'];
      return {
        ...TWEAK_DEFAULTS,
        darkMode: typeof parsed.darkMode === 'boolean' ? parsed.darkMode : TWEAK_DEFAULTS.darkMode,
        accentColor: VALID_COLORS.includes(parsed.accentColor) ? parsed.accentColor : TWEAK_DEFAULTS.accentColor,
        glassRadius: typeof parsed.glassRadius === 'number' && parsed.glassRadius >= 0 && parsed.glassRadius <= 32
          ? parsed.glassRadius : TWEAK_DEFAULTS.glassRadius,
      };
    } catch { return TWEAK_DEFAULTS; }
  });

  const darkMode = tweaks.darkMode;
  const radius = tweaks.glassRadius;
  const lens = darkMode ? LENS.dark : LENS.light;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('onsumn-tweaks-v2', JSON.stringify(tweaks));
  }, [tweaks]);

  useEffect(() => {
    document.documentElement.style.setProperty('--international-orange', tweaks.accentColor);
  }, [tweaks.accentColor]);

  const updateTweak = useCallback((key, val) => {
    setTweaks(prev => ({ ...prev, [key]: val }));
  }, []);

  // Reduced motion — lib/use-reduced-motion.js (plain script, loaded by
  // flows.html before this module). Presence is stable for the whole session,
  // so the hook call count never changes between renders.
  const reducedMotion = window.useReducedMotion ? window.useReducedMotion() : false;

  // ---------------------------------------------------------------------------
  // Core state
  // ---------------------------------------------------------------------------
  const [stations, setStations] = useState([]);
  const [stationsFailed, setStationsFailed] = useState(false); // stations fetch failed → feed E3 raw fallback
  const [edges, setEdges] = useState([]);
  const [graphFailed, setGraphFailed] = useState(false);    // B11 — settled graph-fetch failure (gates E3, no in-flight flash)
  const [headways, setHeadways] = useState(null);           // graph.meta.headways (T10 range mode §3.3)
  const [linesData, setLinesData] = useState(null);         // data/lines.json (T9 draws)
  const [lastTrainData, setLastTrainData] = useState(null); // data/last-train.json (T10 chips)
  const [landmask, setLandmask] = useState(null);           // data/bay-landmask.geojson (T9)
  const [baseGeo, setBaseGeo] = useState(null);             // data/bay-context.geojson (P1 self-basemap)
  const [fares, setFares] = useState(null);                 // data/fares.json (adult Clipper) — lazy, home-gated (effect below)

  // §1 state machine. null = storage not yet reconciled against stations.json.
  const [homeState, setHomeState] = useState(null);
  const [homeStation, setHomeStation] = useState(null);     // the dijkstra source (was activeStation)
  const [reachableCount, setReachableCount] = useState(null); // Goal 5 — live within-the-hour count (feed-pushed); drives the hero pill
  const [gpsSuggest, setGpsSuggest] = useState(null);       // station offered in gps-suggest
  const [selectedStation, setSelectedStation] = useState(null); // the detail target (unchanged)
  const [detailCloseSignal, setDetailCloseSignal] = useState(0); // B8 (K2) — bumped on Esc-close so the feed refocuses the opening row
  const [distances, setDistances] = useState(new Map());
  const [routePrev, setRoutePrev] = useState(new Map());    // predecessor map for T10 route steps
  const [eventMap, setEventMap] = useState(new Map());
  const [visibleSystems, setVisibleSystems] = useState(new Set(['bart', 'caltrain', 'muni', 'vta']));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [liveMessage, setLiveMessage] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverMode, setPopoverMode] = useState('menu');   // 'menu' | 'search'

  // Focus management refs (§6.2, K1/K4)
  const promptHeadingRef = useRef(null);
  const chipRef = useRef(null);
  const ghostChipRef = useRef(null);
  const popoverRef = useRef(null);
  const prevHomeStateRef = useRef(null);
  const focusChipAfterSetRef = useRef(false);
  const focusGhostAfterBrowseRef = useRef(false);
  const homeResolvedRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Deep-link (Phase 2 §2) — /flows?event=<slug> pre-seeds the lens.
  // Parsed ONCE on mount (flows had zero URL handling before this). linkedSlug
  // is held until events + matcher are ready, then resolved through the SAME
  // eventMap the feed/map consume (reverse-index — never a second matcher).
  // ---------------------------------------------------------------------------
  const [linkedSlug] = useState(() => {              // fixed from the arrival URL; never re-set
    try { return new URLSearchParams(window.location.search).get('event') || null; }
    catch { return null; }
  });
  const [eventMatchDone, setEventMatchDone] = useState(false); // matchEventsToStations has run
  const [linkMatch, setLinkMatch] = useState(null);            // { status:'matched', event, station }
  const [linkPendingTitle, setLinkPendingTitle] = useState(null); // N1 prompt kicker (home unset)
  const [linkNotice, setLinkNotice] = useState(null);         // 'not-found' | 'off-grid' (N2/N3)
  const [linkFocusSignal, setLinkFocusSignal] = useState(0);  // desktop → FlowsDetail h2 focus (§2.6)
  const linkMatchDoneRef = useRef(false);        // match resolved to a terminal outcome once
  const linkSelectedRef = useRef(false);         // selectedStation seeded from the link once
  const linkResolveAnnouncedRef = useRef(false); // L1 spoken exactly once (§2.6 no doubles)
  const linkPendingAnnouncedRef = useRef(false); // L4 spoken once
  const skipRouteReadyRef = useRef(false);       // one-shot: suppress the generic desktop route-ready

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const toggleSystem = useCallback((sys) => {
    setVisibleSystems(prev => {
      const next = new Set(prev);
      if (next.has(sys)) next.delete(sys); else next.add(sys);
      return next;
    });
  }, []);

  const announce = useCallback((msg) => { setLiveMessage(msg); }, []);

  // §2.5 U1–U4 — the URL always mirrors the event you're looking at, via
  // replaceState ONLY (controller ruling: zero back-stack entries, so Back
  // returns to the referring card/SSR page — deep-link nav never spams history).
  const syncUrl = useCallback((slug) => {
    try {
      window.history.replaceState(null, '', slug ? `/flows?event=${encodeURIComponent(slug)}` : '/flows');
    } catch { /* history unavailable — page still works, URL just not mirrored */ }
  }, []);

  // ---------------------------------------------------------------------------
  // Load events via shared useEvents hook
  // ---------------------------------------------------------------------------
  const { events, loading: eventsLoading, failed: eventsFailed } = useEvents();

  // ---------------------------------------------------------------------------
  // Load station + graph + lens data on mount.
  // stations/graph are load-bearing (existing behavior). The three lens files
  // degrade gracefully: each failure leaves null and the page still works
  // (spec §4.5 E3 — T10 renders the degraded feed; the map just skips layers).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Independent fetches (phase-review I3): a graph failure must leave
    // stations usable — home resolves, events match, and the feed renders the
    // §4.5 E3 degraded path (graphReady=false) instead of the wrong empty
    // state. A stations failure raises stationsFailed so the feed can fall
    // back to the raw events list.
    fetch('data/stations.json').then(r => {
      if (!r.ok) throw new Error('Failed to load stations.json');
      return r.json();
    }).then(stationData => {
      setStations(stationData.stations || []);
    }).catch(err => {
      console.debug('[FlowsApp] stations load error:', err);
      setStationsFailed(true);
    });

    fetch('data/station-graph.json').then(r => {
      if (!r.ok) throw new Error('Failed to load station-graph.json');
      return r.json();
    }).then(graphData => {
      setEdges(graphData.edges || []);
      // Retain meta.headways for the feed/detail range mode (§3.3) — the graph
      // fetch previously kept only .edges (brief scope item 1).
      setHeadways((graphData.meta && graphData.meta.headways) || null);
    }).catch(err => {
      console.debug('[FlowsApp] graph load error:', err);
      // B11: a SETTLED failure gates the feed's E3 degraded state. Until this is
      // true the feed treats "no edges yet" as still-loading (skeleton), so the
      // "TRAVEL TIMES ARE DOWN" kicker no longer flashes while the graph is in
      // flight — only when it has genuinely failed.
      setGraphFailed(true);
    });

    fetch('data/lines.json').then(r => (r.ok ? r.json() : null))
      .then(setLinesData).catch(() => setLinesData(null));
    fetch('data/last-train.json').then(r => (r.ok ? r.json() : null))
      .then(setLastTrainData).catch(() => setLastTrainData(null));
    fetch('data/bay-landmask.geojson').then(r => (r.ok ? r.json() : null))
      .then(setLandmask).catch(() => setLandmask(null));
    // P1 self-basemap geography (Natural Earth land, committed). Degrades
    // gracefully: a failed fetch leaves the map on its bare water ground.
    fetch('data/bay-context.geojson').then(r => (r.ok ? r.json() : null))
      .then(setBaseGeo).catch(() => setBaseGeo(null));
  }, []);

  // Fares (data/fares.json) load LAZILY — only once a home is set, since a
  // price exists only for a trip — and once per page (lib/flows-fares.js
  // memoizes the read on window). A failed read resolves null: the price is
  // simply absent from every surface, never estimated.
  useEffect(() => {
    if (!homeStation || !window.FlowsFares) return undefined;
    let alive = true;
    window.FlowsFares.load().then((doc) => { if (alive) setFares(doc); });
    return () => { alive = false; };
  }, [homeStation]);

  // ---------------------------------------------------------------------------
  // Re-entry (§1.3): once stations load, reconcile onsumn-home-v1 exactly once.
  // Valid station id → 'set' with NO GPS call; dismissed-empty → 'browse';
  // missing/corrupt/unknown id → 'unset' (graceful re-prompt, AC-1.8).
  // ---------------------------------------------------------------------------
  //
  // ACCOUNT-LITE HANDOFF (§4.2 / F3): when a setup is attached, the local
  // record is hydrated from it BEFORE this resolution runs. That ordering is
  // load-bearing — `homeState` stays null until hydration settles and the
  // home slot renders NOTHING while it is null, so a profile-carrying visitor
  // never sees the SET YOUR HOME BASE prompt flash and vanish. A prompt that
  // appears for one frame is a worse tell than either steady state.
  //
  // There is no narration anywhere on this path: no toast, no status line, no
  // "restored". The set-state simply renders as if the user had always been
  // here — that silence IS the feature presenting. And every failure mode
  // (unattached, offline, 503 while the routes are parked, dead token) lands
  // on exactly the shipped signed-out flow, because ensureHydrated resolves
  // null and the code below is byte-unchanged from what it always did.
  useEffect(() => {
    if (homeResolvedRef.current || stations.length === 0) return;
    homeResolvedRef.current = true;

    const resolve = () => {
      const record = window.readHomeBase ? window.readHomeBase() : null;
      const initial = window.initialHomeState ? window.initialHomeState(record, stations) : 'unset';
      if (initial === 'set') {
        const st = stations.find(s => s.id === record.stationId) || null;
        setHomeStation(st);
        setSelectedStation(st);
      }
      setHomeState(initial);
    };

    const acct = window.OnSumnAccount;
    if (acct && acct.isAttached()) {
      acct.ensureHydrated().then(resolve).catch(resolve);
      return;
    }
    resolve();
  }, [stations]);

  // Attached-state mirror for the two scope-sensitive surfaces below: the
  // contextual door (hidden once attached) and FORGET's sub-line (shown only
  // once attached). Read once at mount; the panel that changes it lives in
  // the footer and publishes through the same subscription.
  const [afyAttached, setAfyAttached] = useState(
    () => !!(typeof window !== 'undefined' && window.OnSumnAccount && window.OnSumnAccount.isAttached())
  );
  useEffect(() => {
    const acct = typeof window !== 'undefined' ? window.OnSumnAccount : null;
    if (!acct || typeof acct.onProfileChange !== 'function') return undefined;
    return acct.onProfileChange(() => setAfyAttached(!!acct.isAttached()));
  }, []);

  // ---------------------------------------------------------------------------
  // State machine driver — all transitions route through lib/home-base.js.
  // ---------------------------------------------------------------------------
  const transition = useCallback((event) => {
    setHomeState(prev => (window.homeTransition ? window.homeTransition(prev, event) : prev));
  }, []);

  // GPS — fires ONLY here, on the FIND MY STATION gesture (never on mount).
  const handleFindMyStation = useCallback(() => {
    if (!navigator.geolocation) {
      // No API at all → straight to the rendered denied path. Guarded like
      // every other window.homeTransition site (phase-review ride-along a).
      if (window.homeTransition) {
        setHomeState(prev => window.homeTransition(window.homeTransition(prev, 'FIND_MY_STATION'), 'GPS_FAIL'));
      }
      announce(COPY.LIVE_DENIED);
      return;
    }
    transition('FIND_MY_STATION');
    announce(COPY.LIVE_FINDING);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const { station, distance } = findNearestStation(latitude, longitude, stations);
        // Coords are dropped right here — only the resolved station survives.
        if (!station) {
          transition('GPS_FAIL');
          announce(COPY.LIVE_DENIED);
        } else if (distance > (window.OUTSIDE_BAY_MILES || 15)) {
          transition('GPS_FAR');
          announce(COPY.LIVE_OUTSIDE);
        } else {
          setGpsSuggest(station);
          transition('GPS_NEAR');
        }
      },
      () => {
        transition('GPS_FAIL');
        announce(COPY.LIVE_DENIED);
      },
      { timeout: 8000, maximumAge: 300000 }
    );
  }, [stations, transition, announce]);

  // Commit a home base (THAT'S ME, manual pick, change-home). Station id only.
  const commitHome = useCallback((station, source) => {
    if (!station) return;
    if (window.setHomeBase) window.setHomeBase(null, station.id, source);
    // F1 — the set gesture's write-through is the ONLY thing that ever
    // populates profile.stationId. No inference path exists: nothing
    // GPS-passive, nothing IP-derived, nothing behavioural writes here.
    // No-ops entirely when no setup is attached.
    if (window.OnSumnAccount) window.OnSumnAccount.pushField('homeStation');
    setHomeStation(station);
    setSelectedStation(station);
    setGpsSuggest(null);
    focusChipAfterSetRef.current = true;
    // THAT'S ME and every pick path land in 'set' (§1.1); homeTransition maps
    // SELECT_STATION from manual-pick/denied/set — force 'set' from gps-suggest.
    setHomeState('set');
    announce(COPY.LIVE_SET + (window.displayStationName ? window.displayStationName(station) : station.name));
  }, [announce]);

  const handleNotNow = useCallback(() => {
    if (window.dismissHomeBase) window.dismissHomeBase();
    focusGhostAfterBrowseRef.current = true;
    transition('NOT_NOW');
  }, [transition]);

  // JUST BROWSING (outside-bay) unifies with NOT NOW: both persist the
  // dismissal (promptDismissedAt) so a reload lands in 'browse', not a fresh
  // 'unset' prompt (T10 controller ruling 5c). Respect the "not now".
  const handleJustBrowsing = useCallback(() => {
    if (window.dismissHomeBase) window.dismissHomeBase();
    focusGhostAfterBrowseRef.current = true;
    transition('JUST_BROWSING');
  }, [transition]);

  const handleForget = useCallback(() => {
    if (window.forgetHomeBase) window.forgetHomeBase();
    // F4 — FORGET while attached clears BOTH. A home that re-appears from the
    // server after a user-initiated forget is a record kept after being asked
    // not to; the scope sub-line beside this control says so in advance.
    if (window.OnSumnAccount && window.OnSumnAccount.isAttached()) {
      const p = window.OnSumnAccount.removeField('homeStation');
      if (p && p.catch) p.catch(() => {});
    }
    setHomeStation(null);
    setSelectedStation(null);
    setPopoverOpen(false);
    setPopoverMode('menu');
    focusGhostAfterBrowseRef.current = true;
    transition('FORGET');
    announce(COPY.LIVE_FORGOT);
  }, [transition, announce]);

  // No-home feed set-home card (§4.6): FIND fires the same GPS gesture, PICK
  // opens the manual picker — both first REOPEN the prompt when we're in the
  // ghost-chip 'browse' state so the flow renders in the home slot above.
  const handleFeedFindMyStation = useCallback(() => {
    setHomeState(prev => (prev === 'browse' ? window.homeTransition(prev, 'REOPEN_PROMPT') : prev));
    handleFindMyStation();
  }, [handleFindMyStation]);
  const handleFeedPickMyself = useCallback(() => {
    setHomeState(prev => {
      const s = prev === 'browse' ? window.homeTransition(prev, 'REOPEN_PROMPT') : prev;
      return window.homeTransition(s, 'PICK_MYSELF');
    });
  }, []);

  // Live region §6.5 — the two T10-deferred announcements. Reachable count
  // lands when the feed computes it; "route ready" on detail open (selection).
  const handleReachableCount = useCallback((n) => {
    setReachableCount(n); // Goal 5 — hero pill binds to the same push the live region gets
    const name = window.displayStationName && homeStation ? window.displayStationName(homeStation) : (homeStation ? homeStation.name : '');
    if (name) announce(`Reachable tonight: ${n} events within an hour of ${name}`);
  }, [homeStation, announce]);

  // Goal 5 — count honesty: a home swap invalidates the previous station's
  // count immediately (pill falls back to the hook until the feed pushes the
  // new number) so a stale N never renders against the wrong home.
  useEffect(() => {
    setReachableCount(null);
  }, [homeStation ? homeStation.id : null]);

  // ---------------------------------------------------------------------------
  // Dijkstra — with route tracking (prev feeds T10's buildRouteSteps)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (homeStation && edges.length > 0) {
      const result = dijkstraWithRoutes(homeStation.id, edges);
      setDistances(result.dist);
      setRoutePrev(result.prev);
    } else {
      setDistances(new Map());
      setRoutePrev(new Map());
    }
  }, [homeStation, edges]);

  // §6.5 "Route ready…" — single string source for BOTH surfaces: the desktop
  // detail-panel effect below, and the mobile feed expansion (feed calls this
  // back when an expansion actually opens — phase-review I2).
  const handleRouteReady = useCallback((minutes) => {
    // One-shot suppression: a deep-link arrival announces its own richer L1
    // ("Route ready: {title}, about {t} minutes from home") and consumes the
    // generic route-ready the selectedStation seed would otherwise fire — so
    // the arrival speaks exactly once (§2.6 no doubles).
    if (skipRouteReadyRef.current) { skipRouteReadyRef.current = false; return; }
    const t = window.ROUND_MINUTES ? window.ROUND_MINUTES(minutes) : Math.round(minutes);
    if (t != null && isFinite(t)) announce(`Route ready: about ${t} minutes from home`);
  }, [announce]);

  // Route-ready announcement, DESKTOP only (§6.5): a genuine detail open — the
  // user tapped a station/event that is NOT their own home; the detail panel
  // renders below the map. Skips the initial resolve (selectedStation ===
  // homeStation). On mobile no detail renders — the feed expansion is the
  // surface that opens, and it owns the announcement (phase-review I2).
  useEffect(() => {
    if (!isMobile && selectedStation && homeStation && selectedStation.id !== homeStation.id) {
      const d = distances.get(selectedStation.id);
      if (d && isFinite(d.time)) handleRouteReady(d.time);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation]);

  // Mobile map-marker tap → feed expand request (phase-review I2): after C1,
  // every marker station has a tonight feed row by construction, so the tap
  // lands somewhere real — the feed scrolls to + expands that station's
  // soonest row. `n` forces effect re-fire on repeat taps of the same marker.
  const [mobileExpandReq, setMobileExpandReq] = useState(null);
  const handleMapSelect = useCallback((s) => {
    setSelectedStation(s);
    if (isMobile && s) setMobileExpandReq({ stationId: s.id, n: Date.now() });
    // U3 — desktop station tap opens station-level detail with NO event context
    // → strip any linked event from the URL (mobile syncs via the feed row open).
    else if (!isMobile) syncUrl(null);
  }, [isMobile, syncUrl]);

  // U2/U3 — the feed reports which event is open (row/expansion) so the shared
  // URL always mirrors it; a mobile expansion close clears it.
  const handleEventOpen = useCallback((event) => { if (event && event.slug) syncUrl(event.slug); }, [syncUrl]);
  const handleEventClose = useCallback(() => { syncUrl(null); }, [syncUrl]);

  // B8 (K2) — desktop route-panel close. Esc closes the actively-opened detail
  // (returns to the default home detail, or none in browse), clears the URL, and
  // bumps a signal the feed uses to return focus to the row that opened it (§6.2
  // focus-return). Second-Enter on the same row toggles closed from the feed
  // side (focus already on the row — no signal needed).
  const closeDesktopDetail = useCallback(() => {
    setSelectedStation(homeStation || null); // back to the default home detail (null if no home)
    syncUrl(null);                           // U3 — no event context
    setDetailCloseSignal((s) => s + 1);
  }, [homeStation, syncUrl]);

  // Esc listener is live ONLY while a NON-home detail panel is open on desktop
  // (the default home detail is not a "route panel" to dismiss). Native document
  // listener so it works whether focus sits on the row or inside the panel; the
  // popover's own Esc handler calls nativeEvent.stopPropagation() first, so it
  // never double-fires with an open popover.
  useEffect(() => {
    if (isMobile || !selectedStation || (homeStation && selectedStation.id === homeStation.id)) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeDesktopDetail(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMobile, selectedStation, homeStation, closeDesktopDetail]);

  // ---------------------------------------------------------------------------
  // Event matching — associate events with nearest stations
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (events.length > 0 && stations.length > 0) {
      // B1: the 0.75mi radius is single-sourced on window.STATION_MATCH_MILES
      // (FlowsTransitLib — read its comment: match radius AND safety gate).
      // EXACTLY TWO readers exist: this call and lib/venue-hub-minutes.js:45.
      // The feed and the deep-link never re-match — they inherit THIS
      // pre-filtered eventMap, so whatever the gate drops here stays dropped
      // everywhere. ("Four surfaces match on the same value" was the old,
      // false version of this comment.)
      const map = matchEventsToStations(events, stations, window.STATION_MATCH_MILES);
      setEventMap(map);
      setEventMatchDone(true); // deep-link resolution waits on this (distinguishes
                               // "matcher ran, no hit" from "matcher hasn't run yet")
    }
  }, [events, stations]);

  // ---------------------------------------------------------------------------
  // Deep-link resolution (§2). Three cooperating pieces:
  //   • eventDayContext — the linked event's OWN dayType + ymd (§2.2: back-by
  //     chip + GETTING BACK key off the event's date, not today). Minutes-from-
  //     home stay date-independent typicals, so the reach figure is untouched.
  //   • buildPinnedRow — the one synthetic §4.3 row for THE ONE YOU CAME FOR,
  //     built with the SAME window helpers a feed bucket row uses, chip tier on
  //     the event's dayType. Only for non-tonight links (tonight uses its real
  //     row). Never injected into eventMap → the map gains no marker (§6.3).
  //   • l1String — the resolved live-region line (title + spoken reach).
  // ---------------------------------------------------------------------------
  const eventDayContext = useCallback((event) => {
    const FF = window.FlowsFeedFormat;
    const parts = (FF && FF.parseEventDateParts) ? FF.parseEventDateParts(event.date, FF.pacificToday()) : null;
    if (!parts) {
      const t = FF ? FF.pacificToday() : { ymd: undefined };
      const dt = window.currentBand ? window.currentBand().dayType : 'weekday';
      return { dayType: dt, ymd: t.ymd };
    }
    const { y, m0, d } = parts;
    const mm = m0 + 1;
    const ymd = '' + y + (mm < 10 ? '0' : '') + mm + (d < 10 ? '0' : '') + d;
    // Weekday via Pacific at UTC-noon of that calendar date (LA is 04–05h, same
    // day) — the currentBand() dayType approach applied to a specific date.
    const dObj = new Date(Date.UTC(y, m0, d, 12));
    let wd;
    try { wd = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short' }).format(dObj); }
    catch { wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dObj.getUTCDay()]; }
    const dayType = wd === 'Sat' ? 'saturday' : wd === 'Sun' ? 'sunday' : 'weekday';
    return { dayType, ymd };
  }, []);

  const buildPinnedRow = useCallback((event, station) => {
    const FF = window.FlowsFeedFormat;
    const d = distances && distances.get ? distances.get(station.id) : null;
    if (!FF || !homeStation || !d || !isFinite(d.time)) return null;
    const reachMin = d.time;
    const steps = window.buildRouteSteps ? window.buildRouteSteps(homeStation.id, station.id, routePrev, stations, linesData) : [];
    const rides = steps.filter((s) => s.kind === 'ride');
    const firstLineId = rides.length ? rides[0].lineId : null;
    const returnLineId = rides.length ? rides[rides.length - 1].lineId : null;
    // C2: a timeUnresolved row (two published clocks, merge refused) has no
    // trustworthy start minute — null fires the EXISTING 'neutral' tier,
    // never 'after-last'. Degrade, don't assert.
    const startMin = event.timeUnresolved === true ? null : FF.parseEventStartMinutes(event.time);
    const dayCtx = eventDayContext(event);                                        // event's own date — for the tier
    // P1 headway date fix: this row exists ONLY for a non-tonight deep link, so
    // "the current band" was always the wrong day here. The headway now answers
    // from the EVENT's dayType + its start-hour band — the same day context the
    // tier grades — or not at all (undefined → plain figure, no range). A
    // date-wrong range is worse than no range.
    const evBand = (startMin != null && window.bandForHour) ? window.bandForHour(Math.floor(startMin / 60)) : null;
    const headway = evBand ? FF.bandHeadway(firstLineId, headways, evBand, dayCtx.dayType) : undefined;
    const tier = (returnLineId && lastTrainData && startMin != null && window.lastTrainTier)
      ? window.lastTrainTier(startMin, returnLineId, dayCtx.dayType, lastTrainData, { todayYmd: dayCtx.ymd })
      : null;
    const chip = tier ? FF.chipInfo(tier) : null;
    const fareCents = (fares && window.FlowsFares) ? window.FlowsFares.tripFareCents(steps, fares, stations) : null;
    return {
      event, station, reachMin,
      band: window.bandForMinutes ? window.bandForMinutes(reachMin) : null,
      startMin, headway, tier, chip, fareCents,
      pinned: true,                          // feed: F10 section + date-led line 2
      tierDayType: dayCtx.dayType, tierYmd: dayCtx.ymd, // FlowsRoutePanel override in the expansion
    };
  }, [distances, homeStation, routePrev, stations, linesData, headways, lastTrainData, fares, eventDayContext]);

  const l1String = useCallback((event, station, minutes) => {
    const FF = window.FlowsFeedFormat;
    const steps = (homeStation && window.buildRouteSteps) ? window.buildRouteSteps(homeStation.id, station.id, routePrev, stations, linesData) : [];
    const firstRide = steps.find((s) => s.kind === 'ride');
    // P1 headway date fix (same class as buildPinnedRow — this announce speaks
    // the linked event's reach): tonight speaks the live band; a non-tonight
    // link answers from the event's own dayType + start-hour band, or speaks a
    // plain figure (undefined headway → no range). Never tonight's range on
    // another day's event.
    let headway;
    if (firstRide && FF) {
      const tonight = FF.isEventToday ? FF.isEventToday(event) : true;
      if (tonight) {
        const now = window.currentBand ? window.currentBand() : { band: 'late', dayType: 'weekday' };
        headway = FF.bandHeadway(firstRide.lineId, headways, now.band, now.dayType);
      } else {
        const startMin = event.timeUnresolved === true ? null : FF.parseEventStartMinutes(event.time); // C2: refused clock → null, 'neutral'
        const evBand = (startMin != null && window.bandForHour) ? window.bandForHour(Math.floor(startMin / 60)) : null;
        headway = evBand ? FF.bandHeadway(firstRide.lineId, headways, evBand, eventDayContext(event).dayType) : undefined;
      }
    }
    const spoken = window.formatReachTimeAria ? window.formatReachTimeAria(minutes, headway) : `about ${Math.round(minutes)} minutes`;
    return `Route ready: ${event.title}, ${spoken} from home`;
  }, [homeStation, routePrev, stations, linesData, headways, eventDayContext]);

  // The feed's linked-event payload — non-null only when home is set + matched.
  // Recomputes on home change (U4: route recomputes from the new home).
  const linkedEventPayload = useMemo(() => {
    if (!linkMatch || linkMatch.status !== 'matched' || homeState !== 'set') return null;
    const { event, station } = linkMatch;
    const FF = window.FlowsFeedFormat;
    const tonight = FF && FF.isEventToday ? FF.isEventToday(event) : false;
    return { event, station, pinnedRow: tonight ? null : buildPinnedRow(event, station) };
  }, [linkMatch, homeState, buildPinnedRow]);

  // Effect 1 — resolve the slug to a terminal MATCH (once). Reverse-indexes the
  // feed's own eventMap (single matcher, single 0.75mi threshold). not-found /
  // off-grid raise the honest notice + polite announce, then strip the param.
  useEffect(() => {
    if (!linkedSlug || linkMatchDoneRef.current) return;
    if (eventsLoading) return;                         // wait for the feed's source
    const raw = (events || []).find((e) => e && e.slug === linkedSlug);
    if (!raw) {
      // B5: distinguish a transient events-fetch FAILURE from a genuine miss. On
      // failure the slug may well live in the (unloaded) feed — do NOT render the
      // N2 "gone" verdict or strip the param, and leave the resolution OPEN (no
      // linkMatchDoneRef) so a later user-events load can still match. Only a
      // SUCCESSFUL load that lacks the slug is "gone."
      if (eventsFailed) return;
      linkMatchDoneRef.current = true;
      setLinkNotice('not-found');
      announce(COPY.LIVE_LINK_NOT_FOUND);              // L2
      syncUrl(null);                                   // U1 strip
      return;
    }
    if (!eventMatchDone) return;                       // wait for matchEventsToStations
    let found = null;
    if (eventMap && eventMap.forEach) {
      eventMap.forEach((evs, stationId) => {
        if (found || !evs) return;
        const hit = evs.find((e) => e && e.slug === linkedSlug);
        if (hit) {
          const st = stations.find((s) => s.id === stationId) || null;
          if (st) found = { station: st, event: hit };
        }
      });
    }
    if (!found) {
      linkMatchDoneRef.current = true;
      setLinkNotice('off-grid');
      announce(COPY.LIVE_LINK_OFF_GRID);               // L3
      syncUrl(null);                                   // U1 strip
      return;
    }
    linkMatchDoneRef.current = true;
    setLinkMatch({ status: 'matched', event: found.event, station: found.station });
  }, [linkedSlug, events, eventsLoading, eventsFailed, eventMatchDone, eventMap, stations, announce, syncUrl, COPY.LIVE_LINK_NOT_FOUND, COPY.LIVE_LINK_OFF_GRID]);

  // Effect 2 — drive arrival behavior from the match + home state (§2.2/§2.3).
  //   set    → seed selectedStation, announce L1 once, hand desktop focus to the
  //            panel h2; the feed pins/scrolls via linkedEventPayload.
  //   browse → select the station (no route, param kept); prompt does NOT reopen.
  //   else   → pending-home: N1 kicker + L4; a later set re-runs → resolved.
  useEffect(() => {
    if (!linkMatch || linkMatch.status !== 'matched') return;
    const { event, station } = linkMatch;
    if (homeState === 'set') {
      if (!linkSelectedRef.current) {
        linkSelectedRef.current = true;
        setSelectedStation(station);
        syncUrl(event.slug);                           // U1/U2 — normalize the shared URL
      }
      setLinkPendingTitle(null);
      const d = distances && distances.get ? distances.get(station.id) : null;
      if (d && isFinite(d.time) && !linkResolveAnnouncedRef.current) {
        linkResolveAnnouncedRef.current = true;
        if (!isMobile) { skipRouteReadyRef.current = true; setLinkFocusSignal((s) => s + 1); } // §2.6 desktop
        announce(l1String(event, station, d.time));    // L1 — the single arrival announcement
      }
    } else if (homeState === 'browse') {
      if (!linkSelectedRef.current) { linkSelectedRef.current = true; setSelectedStation(station); }
      setLinkPendingTitle(null);                       // ghost chip is the path; param kept (§2.1)
    } else {
      setLinkPendingTitle(event.title);                // N1 CLOCKING: {TITLE}
      if (!linkPendingAnnouncedRef.current) {
        linkPendingAnnouncedRef.current = true;
        announce(`Set a home station to see travel time for ${event.title}`); // L4
      }
    }
  }, [linkMatch, homeState, distances, isMobile, announce, syncUrl, l1String]);

  // ---------------------------------------------------------------------------
  // Focus choreography (§6.2, K1/K4): prompt-card variant swaps move focus to
  // the swapped-in heading; landing in 'set' after a user action focuses the
  // HomeChip; landing in 'browse' focuses the ghost chip. Never on first paint.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const prev = prevHomeStateRef.current;
    prevHomeStateRef.current = homeState;
    if (prev === null || homeState === prev) return; // initial resolve — no focus steal
    // 'unset' included so a ghost-chip REOPEN_PROMPT lands focus on the prompt
    // heading (T10 ride-along 5b); initial resolve is guarded above.
    if (['unset', 'locating', 'gps-suggest', 'denied', 'outside-bay', 'manual-pick'].includes(homeState)) {
      if (promptHeadingRef.current) promptHeadingRef.current.focus();
    } else if (homeState === 'set' && focusChipAfterSetRef.current) {
      focusChipAfterSetRef.current = false;
      if (chipRef.current) chipRef.current.focus();
    } else if (homeState === 'browse' && focusGhostAfterBrowseRef.current) {
      focusGhostAfterBrowseRef.current = false;
      if (ghostChipRef.current) ghostChipRef.current.focus();
    }
  }, [homeState]);

  // Popover: focus first control on open; Esc/outside-click close (§1.4).
  useEffect(() => {
    if (!popoverOpen) return;
    const node = popoverRef.current;
    if (node) {
      const first = node.querySelector('button, input');
      if (first) first.focus();
    }
    const onDocDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)
          && chipRef.current && !chipRef.current.contains(e.target)) {
        setPopoverOpen(false);
        setPopoverMode('menu');
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [popoverOpen, popoverMode]);

  const closePopover = useCallback((refocusChip) => {
    setPopoverOpen(false);
    setPopoverMode('menu');
    if (refocusChip && chipRef.current) chipRef.current.focus();
  }, []);

  const handlePopoverKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closePopover(true);
      return;
    }
    if (e.key === 'Tab' && popoverRef.current) {
      // Wrap focus within the dialog (§6.2)
      const focusables = popoverRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href]'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }, [closePopover]);

  // ---------------------------------------------------------------------------
  // Keyframes — injected once (pattern: FlowsSearch). Names are namespaced to
  // avoid colliding with FlowsSearch's own injected block. Values mirror
  // flowsSearchPulse / flowsSearchFindingPulse per spec §1.4/§1.5.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const styleId = 'flows-app-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes flowsHomeFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes flowsHomePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes flowsHomeFindingPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Shared helpers + style fragments
  // ---------------------------------------------------------------------------
  const displayName = (st) => (window.displayStationName ? window.displayStationName(st) : (st ? st.name : ''));

  const getSystem = (station) => {
    if (!station || !station.system) return { label: '?', color: '#888' };
    const info = window.SYSTEM_INFO && window.SYSTEM_INFO[station.system];
    return info || { label: station.system, color: '#888' };
  };

  const capsType = {
    fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12,
    letterSpacing: '0.08em', textTransform: 'uppercase',
  };

  const btnBase = {
    ...capsType,
    minHeight: 44, padding: '10px 18px',
    borderRadius: Math.min(radius, 10),
    cursor: 'pointer',
    transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease',
  };
  const btnSolid = {
    ...btnBase,
    background: darkMode ? '#FF6E32' : '#BF3B00',
    color: darkMode ? '#10110F' : '#fff',
    border: '1px solid transparent',
  };
  const btnOutline = {
    ...btnBase,
    background: 'transparent',
    color: lens.ink,
    border: `1px solid ${lens.ink}`,
  };
  const btnGhostText = {
    ...btnBase,
    background: 'transparent',
    color: lens.muted,
    border: '1px solid transparent',
  };

  const systemBadge = (sys) => (
    <span style={{
      fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
      letterSpacing: '0.08em', textTransform: 'uppercase',
      color: window.categoryTextColor ? window.categoryTextColor(sys.color) : '#fff',
      background: sys.color,
      padding: '3px 8px', borderRadius: 100, flexShrink: 0,
    }}>{sys.label}</span>
  );

  // ---------------------------------------------------------------------------
  // Prompt card (§1.5) — inline, never blocks browsing. One shell, five states.
  // ---------------------------------------------------------------------------
  const renderPromptCard = () => {
    const cardStyle = {
      ...glassStyle(darkMode, 'solid'),
      border: `1px solid ${lens.hairline}`,
      borderRadius: Math.min(radius, 16),
      padding: 20,
      boxShadow: darkMode
        ? '4px 4px 0 0 rgba(255,110,50,0.35)'
        : '4px 4px 0 0 #BF3B00',
      animation: reducedMotion ? 'none' : 'flowsHomeFadeUp 320ms cubic-bezier(0.16,1,0.3,1)',
    };
    const h2Style = {
      fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800,
      textTransform: 'uppercase', color: lens.ink, margin: 0, outline: 'none',
    };
    const h3Style = { ...h2Style, fontSize: 16 };
    const subStyle = {
      fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 400,
      color: lens.muted, lineHeight: 1.55, margin: '8px 0 0',
    };
    const rowStyle = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16, alignItems: 'center' };
    // N1 (§2.3) — one context row above the h2 while a linked event is pending.
    const linkKickerStyle = {
      fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
      letterSpacing: '0.08em', textTransform: 'uppercase',
      color: darkMode ? '#FFB778' : '#8E4E00', marginBottom: 8,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    };

    let body = null;

    if (homeState === 'unset' || homeState === 'locating') {
      body = (
        <React.Fragment>
          <h2 ref={promptHeadingRef} tabIndex={-1} style={h2Style}>{COPY.P1}</h2>
          <p style={subStyle}>{COPY.P2}</p>
          {homeState === 'locating' ? (
            <div style={{ ...rowStyle, minHeight: 44 }}>
              <span style={{
                ...capsType, color: lens.muted,
                animation: reducedMotion ? 'none' : 'flowsHomeFindingPulse 1.8s ease-in-out infinite',
              }}>{COPY.P5}</span>
            </div>
          ) : (
            <div style={rowStyle}>
              <button className="focus-double-ring" style={btnSolid} onClick={handleFindMyStation}>{COPY.FIND}</button>
              <button className="focus-double-ring" style={btnOutline} onClick={() => transition('PICK_MYSELF')}>{COPY.PICK}</button>
              <button className="focus-double-ring" style={btnGhostText} onClick={handleNotNow}>{COPY.NOT_NOW}</button>
            </div>
          )}
        </React.Fragment>
      );
    } else if (homeState === 'gps-suggest' && gpsSuggest) {
      const sys = getSystem(gpsSuggest);
      body = (
        <React.Fragment>
          <h3 ref={promptHeadingRef} tabIndex={-1} style={h3Style}>
            {`LOOKS LIKE ${displayName(gpsSuggest).toUpperCase()}`}
          </h3>
          <p style={{ ...subStyle, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              {`${sys.label.toUpperCase()} · ${(gpsSuggest.city || 'THE BAY').toUpperCase()}`}
            </span>
            {systemBadge(sys)}
          </p>
          <div style={rowStyle}>
            <button className="focus-double-ring" style={btnSolid} onClick={() => commitHome(gpsSuggest, 'gps')}>{COPY.THATS_ME}</button>
            <button className="focus-double-ring" style={btnOutline} onClick={() => transition('PICK_ANOTHER')}>{COPY.PICK_ANOTHER}</button>
          </div>
        </React.Fragment>
      );
    } else if (homeState === 'denied' || homeState === 'manual-pick') {
      const isDenied = homeState === 'denied';
      body = (
        <React.Fragment>
          <h3 ref={promptHeadingRef} tabIndex={-1} style={h3Style}>
            {isDenied ? COPY.P7_H : COPY.P1}
          </h3>
          <p style={subStyle}>{isDenied ? COPY.P7_SUB : COPY.P2}</p>
          <div style={{ marginTop: 16 }}>
            <FlowsSearch
              stations={stations}
              activeStation={null}
              onSelectStation={(s) => commitHome(s, 'manual')}
              gpsStatus={homeState}
              darkMode={darkMode}
              radius={radius}
            />
          </div>
        </React.Fragment>
      );
    } else if (homeState === 'outside-bay') {
      body = (
        <React.Fragment>
          <h3 ref={promptHeadingRef} tabIndex={-1} style={h3Style}>{COPY.P8_H}</h3>
          <p style={subStyle}>{COPY.P8_SUB}</p>
          <div style={rowStyle}>
            <button className="focus-double-ring" style={btnOutline} onClick={handleJustBrowsing}>
              {COPY.JUST_BROWSING}
            </button>
            <button className="focus-double-ring" style={btnSolid} onClick={() => transition('PICK_A_STATION')}>{COPY.PICK_A_STATION}</button>
          </div>
        </React.Fragment>
      );
    }

    return (
      <div style={cardStyle}>
        {linkPendingTitle && (
          <div style={linkKickerStyle}>{`${COPY.CLOCKING}${linkPendingTitle.toUpperCase()}`}</div>
        )}
        {body}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // HomeChip + popover (§1.4) — state 'set'
  // ---------------------------------------------------------------------------
  const renderHomeChip = () => {
    const sys = getSystem(homeStation);
    return (
      <div style={{ position: 'relative' }}>
        <button
          ref={chipRef}
          className="focus-double-ring"
          aria-expanded={popoverOpen}
          onClick={() => { setPopoverMode('menu'); setPopoverOpen(o => !o); }}
          style={{
            ...glassStyle(darkMode, 'solid'),
            ...capsType,
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            minHeight: 48, padding: '10px 16px',
            border: `1px solid ${lens.hairline}`,
            borderRadius: Math.min(radius, 14),
            color: lens.ink, cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span aria-hidden="true" style={{
            width: 8, height: 8, borderRadius: '50%', background: lens.reach1,
            flexShrink: 0,
            animation: reducedMotion ? 'none' : 'flowsHomePulse 2s ease-in-out infinite',
          }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {`HOME · ${displayName(homeStation)}`}
          </span>
          <span aria-hidden="true" style={{ fontSize: 12, color: lens.muted, flexShrink: 0 }}>{'∨'}</span>
        </button>

        {!afyAttached && (
          /* §1.4 — THE ONE contextual door in v1. The user has just created
             the exact datum the feature exists to carry, which makes this the
             single strongest honest moment for it. Same visual grammar as the
             footer door (dotted underline, muted ink, 44px hit via padding).
             It navigates to a named destination the user asked for, so the
             smooth scroll is user-invoked; reduced motion drops it to auto.
             It renders statically and disappears once attached — there is
             nothing to dismiss and nothing that re-asks. */
          <button
            type="button"
            data-afy-flows-door
            className="focus-double-ring"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('onsumn:open-what-follows-you', { detail: { scroll: true } }));
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
              letterSpacing: '0.05em', textTransform: 'uppercase',
              color: lens.muted,
              padding: '16px 2px', margin: '-6px 0 -10px',
              textDecoration: 'underline dotted', textUnderlineOffset: 3,
            }}
          >{COPY.AFY_DOOR}</button>
        )}

        {popoverOpen && (
          <div
            ref={popoverRef}
            role="dialog"
            aria-label="Home base options"
            onKeyDown={handlePopoverKeyDown}
            style={{
              ...glassStyle(darkMode, 'solid'),
              // Goal 1 fix: fully opaque fill. glass 'solid' is only 0.92–0.95
              // alpha, so the CONDITIONS strip beneath bled through. lens.panel
              // is the §0.5 opaque surface token — theme-correct in dark + light,
              // never a hardcoded hex. (solid has no backdrop-filter, so bg is
              // the sole translucency source; border/shadow/radius kept below.)
              background: lens.panel,
              position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 8,
              zIndex: 1500, // dialogs band per reference_zindex_hierarchy
              border: `1px solid ${lens.hairline}`,
              borderRadius: Math.min(radius, 14),
              padding: 14,
              boxShadow: darkMode ? '0 16px 44px rgba(0,0,0,0.5)' : '0 16px 44px rgba(0,0,0,0.16)',
            }}
          >
            {popoverMode === 'menu' ? (
              <React.Fragment>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 2px 12px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: lens.ink,
                  }}>{displayName(homeStation)}</span>
                  {systemBadge(sys)}
                  {homeStation && homeStation.city && (
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500, color: lens.muted }}>
                      {homeStation.city}
                    </span>
                  )}
                </div>
                <button
                  className="focus-double-ring"
                  style={{ ...btnOutline, width: '100%', marginBottom: 8 }}
                  onClick={() => setPopoverMode('search')}
                >{COPY.CHANGE_HOME}</button>
                <button
                  className="focus-double-ring"
                  style={{
                    ...btnBase, width: '100%',
                    background: 'transparent', color: lens.reach1,
                    border: '1px solid currentColor',
                  }}
                  onClick={handleForget}
                >{COPY.FORGET}</button>
                {afyAttached && (
                  /* G6 plain language: a destructive control must let the
                     user predict its reach. While attached, FORGET reaches
                     past this device — so the line says so, before the tap. */
                  <p data-afy-scope style={{
                    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
                    color: lens.muted, margin: '6px 2px 0', lineHeight: 1.5,
                  }}>{COPY.AFY_SCOPE}</p>
                )}
              </React.Fragment>
            ) : (
              <FlowsSearch
                stations={stations}
                activeStation={null}
                onSelectStation={(s) => { commitHome(s, 'manual'); closePopover(true); }}
                gpsStatus={homeState}
                darkMode={darkMode}
                radius={radius}
                hideStatusBar={true}
              />
            )}
            {/* The storage line. Reads the SAME afyAttached flag the scope
                sub-line above reads, so the two can no longer disagree about
                where the station lives — that disagreement was the defect. */}
            <p data-flows-storage-line style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
              color: lens.muted, margin: '12px 2px 0', lineHeight: 1.5,
            }}>{flowsStorageLine(afyAttached)}</p>
          </div>
        )}
      </div>
    );
  };

  // Ghost chip (state 'browse') — the persistent path back to the prompt.
  const renderGhostChip = () => (
    <button
      ref={ghostChipRef}
      className="focus-double-ring"
      onClick={() => transition('REOPEN_PROMPT')}
      style={{
        ...capsType,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        width: '100%', minHeight: 48, padding: '10px 16px',
        background: 'transparent',
        border: `1px dashed ${lens.hairline}`,
        borderRadius: Math.min(radius, 14),
        color: lens.muted, cursor: 'pointer',
      }}
    >{COPY.H2_GHOST}</button>
  );

  // The HomeChip slot — hero → THIS → feed → map (focus order §6.2).
  const renderHomeSlot = () => {
    if (homeState === null) return null; // storage not yet reconciled (first data paint)
    let content;
    if (homeState === 'set') content = renderHomeChip();
    else if (homeState === 'browse') content = renderGhostChip();
    else content = renderPromptCard();
    return (
      // Goal 1 fix: when the HomeChip popover is open, lift the whole slot out
      // of the z:5 tie into the dialog band (reference_zindex_hierarchy 1500–2000)
      // so the dropdown paints above the later CONDITIONS strip sibling (also
      // z:5) instead of colliding with it. Returns to z:5 on close. popoverOpen
      // is only ever true in the 'set' state, so browse/prompt stay at z:5.
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '4px 20px 0', position: 'relative', zIndex: popoverOpen ? 1500 : 5 }}>
        {content}
      </div>
    );
  };

  // Honest-fallback notice (§2.4) — directly under the HomeChip slot. role=status
  // (polite, never assertive per §6.5); persists until dismissed; the page runs
  // as normal /flows behind it (the lens never dies).
  const renderNotice = () => {
    if (!linkNotice) return null;
    const text = linkNotice === 'not-found' ? COPY.N2 : COPY.N3;
    return (
      <div style={{ maxWidth: 720, margin: '8px auto 0', padding: '0 20px', position: 'relative', zIndex: 5 }}>
        <div role="status" style={{
          ...glassStyle(darkMode, 'solid'),
          border: `1px solid ${lens.hairline}`,
          borderRadius: Math.min(radius, 12),
          padding: '12px 16px',
          display: 'flex', alignItems: 'flex-start', gap: 4,
          animation: reducedMotion ? 'none' : 'flowsHomeFadeUp 200ms ease',
        }}>
          <span className="tick-marker tick-marker--amber" aria-hidden="true" style={{ marginTop: 6 }} />
          <span style={{
            flex: 1, fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
            color: lens.ink, lineHeight: 1.5,
          }}>{text}</span>
          <button
            className="focus-double-ring"
            aria-label={COPY.DISMISS_NOTICE}
            onClick={() => setLinkNotice(null)}
            style={{
              flexShrink: 0, minWidth: 44, minHeight: 44, marginRight: -8, marginTop: -8, marginBottom: -8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: '1px solid transparent',
              color: lens.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1,
              borderRadius: Math.min(radius, 10),
            }}
          >✕</button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  // B10-2: detail identity is the PHYSICAL station — the eventMap is keyed by
  // canonicalId ?? id, so resolve the selection the same way (a legacy raw-twin
  // selection still reads the one canonical bucket; skew degrades to raw).
  const selectedKey = selectedStation ? (selectedStation.canonicalId ?? selectedStation.id) : null;
  const selectedEvents = selectedStation ? (eventMap.get(selectedKey) || []) : [];
  const selectedDistance = selectedStation ? (distances.get(selectedKey) || null) : null;

  // Deep-link representative-event override (§2.2): when the open detail IS the
  // linked station, FlowsDetail leads with the linked event's card and keys the
  // back-by chip + GETTING BACK off the event's date (not today).
  const detailLinkedEvent = (linkedEventPayload && selectedStation && linkedEventPayload.station.id === selectedStation.id)
    ? linkedEventPayload.event : null;
  const detailLinkedDayContext = detailLinkedEvent ? eventDayContext(detailLinkedEvent) : null;

  const mapElement = (
    // id + tabIndex are the JUMP TO MAP landing (2026-07-21): the feed's
    // mobile link scrolls here then focuses the region (Iris Option B). The
    // container is a REGION and must NEVER be aria-hidden — only the map's
    // interior panes are hidden from AT (FlowsTransitMap hidePane), and that
    // interior holds no focusable element (markers are keyboard:false) —
    // both pinned in tests/flows-mobile-layout.test.js.
    <div role="region" aria-label={COPY.M3} id="flows-map-region" tabIndex={-1}>
      <FlowsTransitMap
        stations={stations}
        eventMap={eventMap}
        activeStation={homeStation}
        homeStation={homeStation}
        onSelectStation={handleMapSelect}
        visibleSystems={visibleSystems}
        onToggleSystem={toggleSystem}
        darkMode={darkMode}
        height={isMobile ? '56vh' : 520}
        distances={distances}
        lines={linesData}
        lastTrain={lastTrainData}
        landmask={landmask}
        baseGeo={baseGeo}
      />
      {/* C1 — 511 §5(b) LICENSE-REQUIRED: the tag line + link, "in visual
          proximity to the user's access to the Provided Data" (the transit
          graph this page renders: stations, line shapes, last-train). Mirrors
          the Open-Meteo credit (FlowsConditions.jsx). §5(c): this tag line is
          the ONLY permitted use of the 511 mark — never a feature name or
          headline. Guard: tests/attribution-sources.test.js. */}
      <div style={{ marginTop: 6, textAlign: 'right', lineHeight: 1 }}>
        <a
          href="http://www.511.org"
          target="_blank"
          rel="noopener"
          aria-label="Transit data provided by 511.org"
          className="focus-double-ring"
          style={{
            display: 'inline-block',
            fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: lens.muted, textDecoration: 'none',
          }}
        >DATA PROVIDED BY 511.ORG</a>
      </div>
    </div>
  );

  const feedElement = (
    <FlowsEventFeed
      events={events}
      stations={stations}
      eventMap={eventMap}
      activeStation={homeStation}
      homeStation={homeStation}
      visibleSystems={visibleSystems}
      distances={distances}
      lastTrain={lastTrainData}
      lines={linesData}
      headways={headways}
      fares={fares}
      routePrev={routePrev}
      loading={eventsLoading}
      graphReady={edges.length > 0}
      graphFailed={graphFailed}
      stationsFailed={stationsFailed}
      isMobile={isMobile}
      reducedMotion={reducedMotion}
      showSetHomeCard={homeState === 'browse'}
      expandStation={mobileExpandReq}
      linkedEvent={linkedEventPayload}
      closeSignal={detailCloseSignal}
      selectedStation={selectedStation}
      onSelectStation={(s) => setSelectedStation(s)}
      onFindMyStation={handleFeedFindMyStation}
      onPickMyself={handleFeedPickMyself}
      onReachableCount={handleReachableCount}
      onRouteReady={handleRouteReady}
      onEventOpen={handleEventOpen}
      onEventClose={handleEventClose}
      darkMode={darkMode}
      radius={radius}
    />
  );

  const detailElement = selectedStation ? (
    <FlowsDetail
      station={selectedStation}
      activeStation={homeStation}
      stations={stations}
      events={selectedEvents}
      distance={selectedDistance}
      routePrev={routePrev}
      lines={linesData}
      lastTrain={lastTrainData}
      headways={headways}
      fares={fares}
      linkedEvent={detailLinkedEvent}
      linkedDayContext={detailLinkedDayContext}
      focusSignal={linkFocusSignal}
      darkMode={darkMode}
      radius={radius}
    />
  ) : null;

  return (
    <div style={{
      minHeight: '100vh',
      background: lens.page,
      position: 'relative',
      transition: 'background 0.5s',
    }}>
      {/* Radial glow — one large gradient covering the full page */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: darkMode
          ? 'radial-gradient(ellipse at 50% 50%, rgba(255,79,0,0.15) 0%, rgba(213,120,0,0.06) 40%, transparent 75%)'
          : 'radial-gradient(ellipse at 50% 50%, rgba(255,79,0,0.07) 0%, rgba(213,120,0,0.03) 40%, transparent 75%)',
      }} />
      <Header
        darkMode={darkMode}
        activeCity="ALL"
        setActiveCity={function () {}}
        onToggleDark={function () { updateTweak('darkMode', !darkMode); }}
        radius={radius}
        activePage="flows"
      />

      {/* Live region — §6.5. One polite region owned by FlowsApp. All strings
          route through announce(); "Reachable tonight: {n}…" arrives from the
          feed, "Route ready…" from the desktop detail effect or the mobile
          feed expansion (handleRouteReady). */}
      <div aria-live="polite" style={{
        position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
        overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
      }}>{liveMessage}</div>

      <main
        id="main-content"
        tabIndex={-1}
        style={{ maxWidth: 1280, margin: '0 auto', padding: '80px 0 0', position: 'relative', zIndex: 1, outline: 'none' }}
      >
        {/* Bay Flows hero — M1 */}
        <section style={{
          position: 'relative', overflow: 'hidden',
          padding: 'clamp(60px, 8vw, 84px) 32px clamp(20px, 3vw, 28px)',
          textAlign: 'center',
        }}>
          {/* Ambient glow */}
          <div style={{
            position: 'absolute', top: '-20%', left: '30%',
            width: 300, height: 300, borderRadius: '50%',
            background: darkMode ? 'rgba(255,79,0,0.08)' : 'rgba(255,79,0,0.05)',
            filter: 'blur(100px)', pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative', zIndex: 2 }}>
            {/* Pill label */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 16px', marginBottom: 20,
              background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(26,23,20,0.03)',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(26,23,20,0.08)',
              borderRadius: 100,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--international-orange)',
                boxShadow: '0 0 8px rgba(255,79,0,0.5)',
              }} />
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                // Goal 6 audit: raw amber-gold measured 2.88:1 on the light pill
                // chip — the LIVE COUNT was the page's weakest text. Shading the
                // token (no new hex, same lockstep discipline as goal 1's
                // color-mix ring) lands ~6:1. Dark stays amber-gold-light (11:1).
                color: darkMode ? 'var(--amber-gold-light)' : 'color-mix(in srgb, var(--amber-gold) 62%, black)',
              }}>{flowsHeroPill(homeState, reachableCount)}</span>
            </div>
            {/* Title — caps per house rule (M1) */}
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(28px, 5vw, 48px)',
              fontWeight: 900, lineHeight: 1.1,
              letterSpacing: '-0.03em',
              textTransform: 'uppercase',
              color: lens.ink,
              marginBottom: 12,
            }}>BAY FLOWS</h1>
            {/* Subtitle */}
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 400,
              lineHeight: '24px', maxWidth: 460, margin: '0 auto',
              color: lens.muted,
            }}>{COPY.M1_SUB}</p>
          </div>
        </section>

        {/* Home-base slot: chip / prompt / ghost (§1) */}
        {renderHomeSlot()}

        {/* CONDITIONS AT HOME strip (Phase 3 addendum §1.1) — state 'set'
            ONLY (ruling 3): unset/browse/locating/denied/outside-bay render
            no strip, and forget/browse unmounts it. The component owns its
            fetch (Worker-first → direct Open-Meteo, lib/fetch-conditions.js)
            keyed on the home station id, so a home change refetches and a
            toggle-back serves the §3.5 session cache. window-guarded like
            the other lens libs so a missing script degrades to no strip. */}
        {(() => {
          const ConditionsStrip = window.FlowsConditions;
          if (!ConditionsStrip || homeState !== 'set' || !homeStation) return null;
          return (
            <ConditionsStrip
              station={homeStation}
              darkMode={darkMode}
              radius={radius}
              isMobile={isMobile}
              reducedMotion={reducedMotion}
            />
          );
        })()}

        {/* Deep-link honest-fallback notice (§2.4) — under the home slot */}
        {renderNotice()}

        {/* Desktop: feed (left) + map & detail (right) — §4.1. DOM order = feed
            then map (focus order home-chip → feed → map; no CSS reordering). */}
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(320px, 1fr) 1.6fr',
            gap: 24,
            alignItems: 'start',
            padding: '24px 20px 40px',
          }}>
            <div style={{
              // Feed column scrolls itself (§4.1: max-height ≈ map + detail;
              // T10 owns the final feed presentation inside this frame).
              maxHeight: 900, overflowY: 'auto', minWidth: 0,
            }}>
              {feedElement}
            </div>
            <div style={{ minWidth: 0 }}>
              {mapElement}
              {detailElement && (
                <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px 0 0' }}>
                  {detailElement}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile: one flex column (map 56vh — §4.1). NO HOME → the MAP leads
            visually (it's the whoa; 19+ uncapped rows buried it) via CSS order
            ONLY — DOM and tab order stay feed-first (Iris ruling: this
            SATISFIES 1.3.2/2.4.3 and is stronger than a DOM reorder; do NOT
            reorder the JSX). HOME SET → the feed IS the payoff: source order,
            no order styles. Padding follows VISUAL position (16 top / 8 gap /
            40 tail). Desktop grid above is untouched. */}
        {isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={homeState !== 'set'
              ? { order: 2, padding: '8px 16px 40px' }
              : { padding: '16px 16px 8px' }}>
              {feedElement}
            </div>
            <div style={homeState !== 'set'
              ? { order: 1, padding: '16px 16px 8px' }
              : { padding: '8px 16px 40px' }}>
              {mapElement}
            </div>
          </div>
        )}
      </main>

      <Footer
        darkMode={darkMode}
        radius={radius}
      />
    </div>
  );
}

window.FlowsApp = FlowsApp;
window.flowsHeroPill = flowsHeroPill; // Goal 5 — pure pill-text contract, pinned by tests
window.flowsStorageLine = flowsStorageLine; // B1 — conditional storage-honesty contract, pinned by tests
